/**
 * Pfand-values settings page — ``/settings/pfand-values`` (§10.4).
 *
 * Uses the shared ``<ManagedListTable>`` for DnD reorder, archive/restore
 * and inline edit. Pfand values carry an extra ``cents`` field beyond the
 * shared "label" — it's rendered as € in the table but stored as integer
 * cents on the wire.
 *
 * Snapshots stored on refill_line / expense rows preserve history, so
 * editing a row's label or cents is safe — only future writes pick up
 * the new value.
 */

import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Modal,
  Select,
  Tag,
  Typography,
  notification,
} from "antd";
import { NumInput as InputNumber } from "../../components/NumInput";
import type { ColumnsType } from "antd/es/table";

import { ApiError, type PfandValueRow, api } from "../../api/client";
import { ManagedListTable } from "../../components/settings/ManagedListTable";
import { formatCents } from "../../utils/dateFormat";

const { Text } = Typography;

interface FormValues {
  label: string;
  cents: number;
  kind: "german" | "onsite";
}

export const PfandValuesPage = () => {
  const [rows, setRows] = useState<PfandValueRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PfandValueRow | null>(null);
  const [form] = Form.useForm<FormValues>();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.listPfandValues(true));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ label: "", cents: 0, kind: "german" });
    setModalOpen(true);
  };

  const openEdit = (row: PfandValueRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({ label: row.label, cents: row.cents, kind: row.kind });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const payload: { label?: string; cents?: number } = {};
        if (values.label.trim() !== editing.label) {
          payload.label = values.label.trim();
        }
        if (values.cents !== editing.cents) {
          payload.cents = values.cents;
        }
        if (Object.keys(payload).length > 0) {
          await api.patchPfandValue(editing.id, payload, editing.updated_at);
        }
      } else {
        await api.createPfandValue({
          label: values.label.trim(),
          cents: values.cents,
          sort_order: rows.length * 10,
          kind: values.kind,
        });
      }
      setModalOpen(false);
      setEditing(null);
      await refresh();
    } catch (err) {
      notification.error({
        message: editing ? "Update failed" : "Create failed",
        description: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const onArchive = async (row: PfandValueRow) => {
    setBusy(true);
    try {
      await api.deletePfandValue(row.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (row: PfandValueRow) => {
    setBusy(true);
    try {
      await api.patchPfandValue(row.id, { active: true }, row.updated_at);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onReorder = async (orderedIds: number[]) => {
    try {
      await api.reorderPfandValues(orderedIds);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      await refresh();
    }
  };

  // The extra column slots into the ManagedListTable between the label
  // and actions columns. We format cents as € for display; the modal
  // edits the underlying integer cents directly.
  const extraColumns: ColumnsType<PfandValueRow> = [
    {
      title: "Value",
      key: "cents",
      width: 120,
      align: "right",
      render: (_: unknown, row: PfandValueRow) => (
        <Text>{formatCents(row.cents)}</Text>
      ),
    },
    {
      title: "Kind",
      key: "kind",
      width: 110,
      render: (_: unknown, row: PfandValueRow) => (
        <Tag color={row.kind === "onsite" ? "blue" : "default"}>
          {row.kind === "onsite" ? "On-site" : "German"}
        </Tag>
      ),
    },
  ];

  return (
    <>
      <ManagedListTable<PfandValueRow>
        title="Pfand values"
        description="Bottle deposit values (label + cents). Snapshots stored on refill / expense rows preserve history."
        rows={rows}
        loading={loading}
        busy={busy}
        error={error}
        onErrorDismiss={() => setError(null)}
        addLabel="+ New Pfand value"
        labelOf={(r) => r.label}
        keyOf={(r) => r.id}
        isArchived={(r) => !r.active}
        isSystem={() => false}
        extraColumns={extraColumns}
        onReorder={onReorder}
        onCreate={openCreate}
        onEdit={openEdit}
        onArchive={onArchive}
        onRestore={onRestore}
      />
      <Modal
        title={editing ? "Edit Pfand value" : "New Pfand value"}
        open={modalOpen}
        onOk={() => void onSubmit()}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        okText={editing ? "Save" : "Create"}
        confirmLoading={busy}
        destroyOnHidden
      >
        <Form<FormValues> form={form} layout="vertical">
          <Form.Item
            label="Label"
            name="label"
            rules={[{ required: true, message: "Label is required" }]}
          >
            <Input maxLength={64} placeholder="e.g. Glasflasche 0,5 L" autoFocus />
          </Form.Item>
          {/* Kind is fixed at creation (re-pointing a value with outstanding
              deposits is rejected) — so it's editable only on create. */}
          <Form.Item
            label="Kind"
            name="kind"
            tooltip="German = kiosk DPG/bottle deposit. On-site = the POS bottle pfand, assignable to an article in Catalog → POS."
          >
            <Select
              disabled={editing !== null}
              options={[
                { value: "german", label: "German (kiosk DPG / bottle)" },
                { value: "onsite", label: "On-site (POS bottle pfand)" },
              ]}
            />
          </Form.Item>
          <Form.Item
            label="Cents"
            name="cents"
            rules={[
              { required: true, message: "Cents is required" },
              {
                validator: (_, v) =>
                  Number.isInteger(Number(v)) && Number(v) >= 0
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("must be a non-negative integer"),
                      ),
              },
            ]}
            tooltip="Stored as integer cents (e.g. 8 for €0.08)."
          >
            <InputNumber min={0} step={1} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PfandValuesPage;
